import { useTranslation } from "react-i18next";
import * as React from "react";

import { Menu, MenuActionItem, MenuLinkItem } from "../../Menu";
import { createTestId } from "../../TestableElement";
import { useResourceHubNodesListContext } from "../contexts/NodesListContext";
import { DeleteResourceConfirmModal } from "../DeleteResourceConfirmModal";
import { getResourceName } from "../selectors";
import type { ResourceHubLink } from "../types";
import { MoveResourceMenuItem, MoveResourceModal } from "./MoveResource";

interface LinkMenuProps {
  link: ResourceHubLink;
}

export function LinkMenu({ link }: LinkMenuProps) {
  const { permissions } = useResourceHubNodesListContext();
  const [showMoveForm, setShowMoveForm] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const toggleMoveForm = () => setShowMoveForm((value) => !value);
  const toggleDeleteModal = () => setShowDeleteModal((value) => !value);

  if (!permissions) return null;

  const relevantPermissions = [permissions.canEditParentFolder, permissions.canEditLink, permissions.canDeleteLink];
  const menuId = createTestId("menu", link.id);

  if (!relevantPermissions.some(Boolean)) return null;

  return (
    <>
      <Menu size="medium" testId={menuId}>
        {permissions.canEditParentFolder && <MoveResourceMenuItem resource={link} showModal={toggleMoveForm} />}
        {permissions.canEditLink && <EditLinkMenuItem link={link} />}
        {permissions.canDeleteLink && <DeleteLinkMenuItem link={link} toggleDeleteModal={toggleDeleteModal} />}
      </Menu>

      <MoveResourceModal resource={link} resourceType="link" isOpen={showMoveForm} hideModal={toggleMoveForm} />
      <DeleteLinkModal link={link} isOpen={showDeleteModal} hideModal={toggleDeleteModal} />
    </>
  );
}

function EditLinkMenuItem({ link }: LinkMenuProps) {
  const { t } = useTranslation();
  const { paths } = useResourceHubNodesListContext();

  if (!paths) return null;

  const editPath = paths.editLinkPath(link.id);
  const editId = createTestId("edit", link.id);

  return (
    <MenuLinkItem to={editPath} testId={editId}>
      {t("Edit")}
    </MenuLinkItem>
  );
}

function DeleteLinkMenuItem({ link, toggleDeleteModal }: { link: ResourceHubLink; toggleDeleteModal: () => void }) {
  const { t } = useTranslation();
  const deleteId = createTestId("delete", link.id);

  return (
    <MenuActionItem onClick={toggleDeleteModal} testId={deleteId} danger>
      {t("Delete")}
    </MenuActionItem>
  );
}

function DeleteLinkModal({
  link,
  isOpen,
  hideModal,
}: {
  link: ResourceHubLink;
  isOpen: boolean;
  hideModal: () => void;
}) {
  const { onRefetch, actions } = useResourceHubNodesListContext();

  const handleDelete = async () => {
    const deleteLink = actions.deleteLink;

    if (!deleteLink) return;

    await deleteLink(link.id);
    onRefetch?.();
    hideModal();
  };

  return (
    <DeleteResourceConfirmModal
      isOpen={isOpen}
      onClose={hideModal}
      resourceType="link"
      resourceName={getResourceName(link)}
      onConfirm={handleDelete}
    />
  );
}
